import { Link } from "react-router-dom";
import { PageHeader } from "@/components/data-display/page-header";
import { StatCardRow } from "@/components/data-display/stat-card";
import { TableState } from "@/components/data-display/table-state";
import { WipHint } from "@/components/ui/wip-hint";
import { Table, TableWrapper } from "@/components/ui/table";
import { buttonVariants } from "@/components/ui/button";
import { WipNotice, WipReason, WipPanel } from "@/features/treasury/treasury-wip";

/**
 * LND-00 Lending Overview — designed layout, no backend in this environment
 * (design-port round 3, 2026-10-03).
 *
 * Ported from `.design-ref/prototype/features/treasury/treasury.tsx`
 * (`LendingOverview`) with its sample positions, health factors and margin
 * warning removed: supply and borrow positions were previously fabricated here
 * (page-delivery-pipeline.md G6, 2026-09-23). The designer's header, stat row,
 * positions table and its two entry points are kept; the stat values render as
 * unknown rather than as a number, the table renders its designed empty row,
 * and the page issues no API call (see context/page_pipeline/BACKEND_GAPS.md).
 * The designer's disposition legend is not ported: it documents statuses that
 * only exist in the wireframe's fixtures.
 */

const DEFERRED_TITLE = "Lending is not available in this environment yet";
const DEFERRED_DESCRIPTION =
  "Lending and borrowing positions, collateral health, and margin monitoring have no backend in this environment yet. This page will show real positions once the lending domain is built.";

const UNAVAILABLE = "Not available yet";

/** The eight columns the designer's positions table declares. */
const POSITION_COLUMNS = [
  "ID",
  "Type",
  "Asset",
  "Amount",
  "Venue / Route",
  "Collateral",
  "Health Factor",
  "Status",
];

export default function TreasuryLendingPage() {
  return (
    <div>
      <PageHeader
        title="Lending"
        subtitle="Supply and borrow positions, health factors, and margin status across lending venues."
        actions={<WipHint />}
      />
      <WipReason />
      <WipNotice title={DEFERRED_TITLE} description={DEFERRED_DESCRIPTION} />

      <StatCardRow
        className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
        stats={[
          { value: "", label: "Total Supplied", hint: UNAVAILABLE },
          { value: "", label: "Total Borrowed", hint: UNAVAILABLE },
          { value: "", label: "Avg Health Factor", hint: UNAVAILABLE },
          { value: "", label: "Margin Warnings", hint: UNAVAILABLE },
        ]}
      />

      <WipPanel title="Lending Positions" bodyClassName="p-0" className="mb-5">
        <TableWrapper className="rounded-none border-0">
          <Table className="dense">
            <thead>
              <tr>
                {POSITION_COLUMNS.map((column) => (
                  <th key={column} scope="col">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <TableState
                variant="empty"
                colSpan={POSITION_COLUMNS.length}
                title="No lending positions"
                description="Positions are read from the lending backend, which does not exist in this environment yet."
              />
            </tbody>
          </Table>
        </TableWrapper>
      </WipPanel>

      <div className="flex flex-wrap items-center gap-2">
        <Link to="/treasury/lending/supply" className={buttonVariants({ size: "md" })}>
          New Supply
        </Link>
        <Link to="/treasury/lending/borrow" className={buttonVariants({ size: "md", variant: "secondary" })}>
          New Borrow
        </Link>
      </div>
    </div>
  );
}
